// JAWS vs VoiceOver (see doc/jaws-voiceover-accessibility-reference.md §5):
// aria-pressed on preference cards is sufficient for selection feedback.

import React, { useState } from 'react'
import { useData } from './DataProvider'
import useTheme from '../shared/ThemeProvider'
import { Box } from '@mui/joy'
import { LeafIcon, PlantIcon, TreeIcon } from './Icons'
import { NavButton, ButtonContainer } from './Buttons'
import { SpinnerWhite } from '../shared/Spinner'
import useTranslation from '../shared/TranslationProvider'
import { getApi } from '../../util/api'
import StepProgressHeading from './StepProgressHeading'
import { stepDescribedBy } from './stepProgress'

const Step5 = () => {
  const {
    notificationPreference,
    setNotificationPreference,
    setCurrentStep,
    candidate,
    step,
  } = useData()
  const { t } = useTranslation()

  const [finished, setFinished] = useState(false)
  const handleFinish = () => {
    setFinished(true)
    setCurrentStep(6)
  }

  return (
    <>
      <div data-testid="a11y-onboarding--step-5">
        <Box className="pt-10">
          {/* CTS-9266 — YNS-020: one h1 per step */}
          <h1
            data-onboarding-step-heading
            tabIndex={-1}
            aria-describedby={stepDescribedBy('onboarding-step5-subtitle')}
            className="text-2xl font-bold focus:outline-none md:text-4xl"
          >
            {t('step5.title')}
          </h1>
          <StepProgressHeading>
            {t('stepper_progress', { current: step })}
          </StepProgressHeading>
          <p
            id="onboarding-step5-subtitle"
            className="py-4 text-base md:text-xl"
          >
            {t('step5.subtitle')}
          </p>
        </Box>

        <NotificationPreferenceSelector
          notificationPreference={notificationPreference}
          setNotificationPreference={setNotificationPreference}
          candidate={candidate}
        />
      </div>

      <ButtonContainer>
        <NavButton type="back" onClick={() => setCurrentStep(4)}>
          {t('back')}
        </NavButton>
        <NavButton type="submit" onClick={handleFinish} disabled={finished}>
          {finished ? (
            <SpinnerWhite className="flex items-center" show />
          ) : (
            t('finished')
          )}
        </NavButton>
      </ButtonContainer>
    </>
  )
}

export const NotificationPreferenceSelector = ({
  notificationPreference,
  setNotificationPreference,
  candidate,
}) => {
  const { t } = useTranslation()
  const { primaryColor, platformName } = useTheme()

  const isNonUsCandidate = !candidate.isUsCandidate
  const recommendedSx = {
    border: `2px solid ${primaryColor}`,
    backgroundColor: '#fff',
    color: '#08467C',
  }
  const activeRecommendedSx = {
    border: '2px solid #fff',
    backgroundColor: primaryColor,
    color: '#fff',
  }
  const containerSx = {
    border: `2px solid ${primaryColor}`,
    marginTop: '50px',
    borderRadius: '10px',
  }
  const logoSx = {
    position: 'relative',
    top: '-66px',
    borderRadius: '52px',
    border: `2px solid ${primaryColor}`,
    padding: '25px',
    width: '100px',
    marginBottom: '-40px',
    height: '100px',
    backgroundColor: '#fff',
  }
  const activeContainerSx = {
    ...containerSx,
    backgroundColor: primaryColor,
    color: '#fff',
  }
  const handleClick = (preference) => {
    setNotificationPreference(preference)

    getApi().patch('/onboarding/', {
      candidate: {
        candidate_profile_attributes: {
          notification_preference: preference,
        },
      },
    })
  }

  const cardFocusClass =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'

  return (
    <Box
      className="grid gap-8 pt-10 md:grid-cols-3"
      data-testid="a11y-onboarding--notification-preferences"
    >
      {/* CTS-9205 / CTS-9266 — YNS-018 & YNS-020: native button, aria-pressed; level-2 headings (role) inside card for outline */}
      <Box
        component="button"
        type="button"
        className={`m-0 flex cursor-pointer flex-col items-center border-0 p-4 text-center ${cardFocusClass}`}
        sx={
          notificationPreference === 'basic' ? activeContainerSx : containerSx
        }
        onClick={() => handleClick('basic')}
        aria-pressed={notificationPreference === 'basic'}
      >
        <Box sx={logoSx} aria-hidden="true">
          <LeafIcon />
        </Box>
        <span className="text-2xl font-semibold" role="heading" aria-level="2">
          {t('step5.basic.title')}
        </span>
        <div className="flex h-full flex-col justify-between">
          <p className="p-2 text-sm">{t('step5.basic.detail1')}</p>
          <p className="p-2 text-sm">{t('step5.basic.detail2')}</p>
        </div>
      </Box>
      <Box
        component="button"
        type="button"
        className={`relative m-0 flex cursor-pointer flex-col items-center border-0 p-4 text-center ${cardFocusClass}`}
        sx={
          notificationPreference === 'standard'
            ? activeContainerSx
            : containerSx
        }
        onClick={() => handleClick('standard')}
        aria-pressed={notificationPreference === 'standard'}
      >
        <Box sx={logoSx} aria-hidden="true">
          <PlantIcon />
        </Box>
        <Box
          className="absolute left-2 top-4 rounded-3xl px-4 py-3 text-sm font-bold"
          sx={
            notificationPreference === 'standard'
              ? recommendedSx
              : activeRecommendedSx
          }
        >
          {t('step5.recommended')}
        </Box>
        <span className="text-2xl font-semibold" role="heading" aria-level="2">
          {t('step5.standard.title')}
        </span>
        <div className="flex h-full flex-col justify-between">
          <p className="p-2 text-sm">{t('step5.standard.detail')}</p>
          {isNonUsCandidate && (
            <p className="p-2 text-sm">{t('step5.standard.detail_non_us')}</p>
          )}
        </div>
      </Box>
      <Box
        component="button"
        type="button"
        className={`m-0 flex cursor-pointer flex-col items-center border-0 p-4 text-center ${cardFocusClass}`}
        sx={
          notificationPreference === 'comprehensive'
            ? activeContainerSx
            : containerSx
        }
        onClick={() => handleClick('comprehensive')}
        aria-pressed={notificationPreference === 'comprehensive'}
      >
        <Box sx={logoSx} aria-hidden="true">
          <TreeIcon />
        </Box>
        <span className="text-2xl font-semibold" role="heading" aria-level="2">
          {t('step5.comprehensive.title')}
        </span>
        <div className="flex h-full flex-col justify-between">
          <p className="p-2 text-sm">
            {t('step5.comprehensive.detail', { yns: platformName })}
          </p>
          {isNonUsCandidate && (
            <p className="p-2 text-sm">
              {t('step5.comprehensive.detail_non_us')}
            </p>
          )}
        </div>
      </Box>
    </Box>
  )
}

export default Step5
